export interface WebHostingAiCapability {
  enabled: boolean;
  eligible: boolean;
  dailyLimit: number;
  remaining: number;
  promptMaxChars: number;
}

export interface WebHostingAiResult {
  html: string;
}

export type WebHostingAiTaskState = 'pending' | 'processing' | 'success' | 'failed';

export interface WebHostingAiTaskStatus {
  taskId: string;
  status: WebHostingAiTaskState;
  previewCode?: string;
  reasoningPreview?: string;
  result?: WebHostingAiResult;
  errorCode?: string;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  code?: string;
  data: T;
}

interface AcceptedTask {
  taskId: string;
  status: 'pending';
}

export class WebHostingAiApiError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'WebHostingAiApiError';
    this.code = code;
  }
}

const parseResponse = async <T>(response: Response): Promise<T> => {
  let result: ApiResponse<T>;
  try {
    result = await response.json();
  } catch {
    throw new WebHostingAiApiError('AI web page service returned an invalid response');
  }
  if (!response.ok || !result.success) {
    throw new WebHostingAiApiError(result.message || 'AI web page request failed', result.code);
  }
  return result.data;
};

export const getWebHostingAiCapability = async (): Promise<WebHostingAiCapability> => {
  const response = await fetch('/api/web-hosting/ai/capability', {
    credentials: 'include',
    cache: 'no-store',
  });
  return parseResponse<WebHostingAiCapability>(response);
};

export const submitWebHostingAiTask = async (prompt: string): Promise<AcceptedTask> => {
  const response = await fetch('/api/web-hosting/ai/tasks', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ prompt }),
  });
  return parseResponse<AcceptedTask>(response);
};

export const getWebHostingAiTaskStatus = async (taskId: string): Promise<WebHostingAiTaskStatus> => {
  const response = await fetch(`/api/web-hosting/ai/tasks/${encodeURIComponent(taskId)}`, {
    credentials: 'include',
    cache: 'no-store',
  });
  return parseResponse<WebHostingAiTaskStatus>(response);
};

export const consumeWebHostingAiTask = async (taskId: string): Promise<void> => {
  const response = await fetch(`/api/web-hosting/ai/tasks/${encodeURIComponent(taskId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await parseResponse<void>(response);
};
